import { defineStore } from "pinia";
import { usePwaStore } from "@/stores/pwa";
import { useAuthStore } from "@/stores/auth";
import axios from "@/plugins/axios";
import type { Client } from "@/types/client";

/**
 * A store module for managing the Client resource.
 *
 * In the following, when interacting with the API, errors are handled by:
 * 1. setting requireSync to true to recover via syncing
 * 2. rethrow the error for console output and for BugSnag
 */
export const useClientStore = defineStore("client", () => {
	/**
	 * Store a client by calling the API client store endpoint.
	 */
	const store = async function (client: Client): Promise<void> {
		const authStore = useAuthStore();
		const pwaStore = usePwaStore();

		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		client.created_at = isoString;

		// Update the current user store
		authStore.user.clients.push(client);

		// Try to persist it
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.post(route("api.client.store"), client).then((response) => {
				// the server sets its own created_at/updated_at on create -
				// adopt them so a later sync of this now-existing record
				// isn't compared against our own stale local values
				const clientReturned = response.data.data;
				client.created_at = clientReturned.created_at;
				client.updated_at = clientReturned.updated_at;
			}).catch(error=>{
				pwaStore.requireSync = true;
				console.error(error.code);
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}
		return;
	};

	/**
	 * Update a client by calling the API client update endpoint.
	 */
	const update = async function (client: Client): Promise<void> {
		const pwaStore = usePwaStore();
		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		client.updated_at = isoString;

		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.patch(route("api.client.update", { id: client.id }), client).then((response) => {
				// the server overwrites updated_at with its own timestamp on
				// every save, regardless of what we sent - adopt it so the
				// next sync isn't comparing against our own stale local value
				client.updated_at = response.data.data.updated_at;
			}).catch(error=>{
				pwaStore.requireSync = true;
				console.error(error.code);
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}
	};

	/**
	 * Destroy client by calling the API client destroy endpoint.
	 */
	const destroy = async function (client: Client): Promise<void> {
		const pwaStore = usePwaStore();
		const authStore = useAuthStore();

		// Mark the client as deleted
		client.deleted_at = new Date().toISOString();//.replace("T", " ");
		client.updated_at = new Date().toISOString();//.replace("T", " ");

		// Try to persist it
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.delete(route("api.client.destroy", { id: client.id })).then(() => {
				// console.log("deleted client");
				// Remove local soft-deleted clients by refetching data
				authStore.getCurrentUser();
			}).catch(error=>{
				pwaStore.requireSync = true;
				console.error(error.code);
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}
	};

	return { store, update, destroy };
}, {
	persist: {
		// Ensure none of this store is persisted. Clients are persisted via the
		// current user store.
		paths: []
	}
});
